/**
 * FmReportComposition — report/data-display layout primitives, split from
 * FmLayout: FmReportNav, FmSectionBand, FmReportSection.
 * Owns the sticky report nav + full-bleed section bands + their content rail.
 */
import React from 'react';
import {
  cx,
  resolveSectionTheme,
  sectionBandInnerMaxWidth,
  type FmContentWidthSize,
} from './fmLayoutShared';
import {
  ReportNavBackAction,
  type ReportNavBackActionDescriptor,
} from './ReportNavBackAction';
import {
  FmHostedHeadingProvider,
  plainHeadingText,
  useSectionHostedHeading,
} from './fmHostedHeading';
import { isUnmodifiedAnchorActivation, useSurfaceAnchorAlignment } from './FmReportNav.anchorAlignment';
import { useReportNavScrollSpy } from './FmReportNav.scrollSpy';
import { useReportNavTrack } from './FmReportNav.track';
import { useAdaptiveNavTheme } from '@/shared/ui/useAdaptiveNavTheme';
import './FmLayout.css';

// FmReportNav

export interface FmReportNavItem {
  id: string;
  label: React.ReactNode;
  /** Shorter words for the narrow (mobile) track; the full label stays the accessible name. */
  shortLabel?: string;
  meta?: React.ReactNode;
  disabled?: boolean;
}

export interface FmReportNavProps {
  /** Section anchors rendered by the canonical report/navigation rail. */
  items: FmReportNavItem[];
  /** Active section id. */
  activeId?: string;
  /** Semantic navigation tone. */
  tone?: 'light' | 'dark';
  sticky?: boolean;
  /**
   * Sticky offset owned by the enclosing shell. The app shell resolves
   * `--fm-report-nav-dock-top` to the bar's real measured height; the static
   * `--fm-topbar-h` token remains the pre-layout / no-shell fallback.
   */
  offsetTop?: number | string;
  /**
   * `surface` (default): solid pill bar.
   * `docked`: transparent, centered text nav that docks directly under the top
   * bar and flips its own data-theme to the band behind it
   * (preview/topbar.html). Active item is the only highlight.
   */
  variant?: 'surface' | 'docked';
  /**
   * Backing for the `docked` variant. `glass` (default) is the transparent,
   * blurred strip that lets the band behind show through. `solid` swaps in the
   * adaptive semantic surface token (`--fm-d-surface`, which flips with the
   * nav's own scroll-detected data-theme) so scrolled content can no longer
   * ghost through the pinned bar and occlude its labels.
   * Ignored for the `surface` variant, which is already solid.
   */
  dockedSurface?: 'glass' | 'solid';
  /**
   * @deprecated DS-internal escape hatch for isolated previews/tests. Docked
   * report navs own adaptive theme detection internally so pages do not
   * hand-wire nav colour.
   */
  adaptiveTheme?: 'dark' | 'light';
  ariaLabel?: string;
  onSelect?: (id: string) => void;
  /** Origin-aware report return action rendered in the canonical nav rail. */
  backAction?: ReportNavBackActionDescriptor;
  /**
   * `anchor` preserves native hash navigation after `onSelect` observes the
   * click. `controlled` lets a descriptor-owned shell manage URL/history and
   * suppresses the additional native hash entry.
   */
  navigationBehavior?: 'anchor' | 'controlled';
  /** Optional prefix for item test ids; defaults to the nav test id. */
  testIdPrefix?: string;
  /**
   * @internal DS/migration class hook; forbidden in route-owned code. Add a
   * named `variant` or `tone` instead of styling navigation locally.
   */
  className?: string;
  'data-testid'?: string;
}

const ReportNavItem: React.FC<{
  item: FmReportNavItem;
  activeId?: string;
  testIdPrefix: string;
  onSelect?: (id: string) => void;
  navigationBehavior: NonNullable<FmReportNavProps['navigationBehavior']>;
  onAnchorSelect: (id: string) => void;
}> = ({ item, activeId, testIdPrefix, onSelect, navigationBehavior, onAnchorSelect }) => {
  const isActive = item.id === activeId;
  return (
    <a
      className={cx(
        'fm-report-nav__item',
        isActive && 'fm-report-nav__item--active',
        item.disabled && 'fm-report-nav__item--disabled',
      )}
      href={item.disabled ? undefined : `#${item.id}`}
      aria-current={isActive ? 'page' : undefined}
      aria-disabled={item.disabled ? true : undefined}
      data-testid={`${testIdPrefix}-${item.id}`}
      onClick={(event) => {
        if (item.disabled) {
          event.preventDefault();
          return;
        }
        if (navigationBehavior === 'anchor' && !isUnmodifiedAnchorActivation(event)) return;
        if (onSelect) {
          if (navigationBehavior === 'controlled') event.preventDefault();
          onSelect(item.id);
        }
        if (navigationBehavior === 'anchor') onAnchorSelect(item.id);
      }}
    >
      {item.shortLabel ? (
        <>
          <span className="fm-report-nav__label fm-report-nav__label--full">{item.label}</span>
          <span className="fm-report-nav__label fm-report-nav__label--short" aria-hidden="true">
            {item.shortLabel}
          </span>
        </>
      ) : (
        <span className="fm-report-nav__label">{item.label}</span>
      )}
      {item.meta ? <span className="fm-report-nav__meta">{item.meta}</span> : null}
    </a>
  );
};

/** Opt-in opaque backing class for a docked report nav.
 *  Kept as a module helper so the branch lives here, not in the FmReportNav body. */
const dockedSolidClass = (
  isDocked: boolean,
  dockedSurface: FmReportNavProps['dockedSurface'],
): string | false => isDocked && dockedSurface === 'solid' && 'fm-report-nav--docked-solid';

/** The section under the nav is the active entry unless the route controls
 *  the selection itself; on an anchor nav the scroll position is the truth and
 *  `activeId` only names the entry before the reader scrolls. */
const spiesActiveSection = (
  sticky: boolean,
  activeId: string | undefined,
  behavior: FmReportNavProps['navigationBehavior'],
): boolean => sticky && (activeId === undefined || behavior !== 'controlled');

const resolvedNavigationBehavior = (
  behavior: FmReportNavProps['navigationBehavior'],
): NonNullable<FmReportNavProps['navigationBehavior']> =>
  behavior === 'controlled' ? 'controlled' : 'anchor';

export const FmReportNav: React.FC<FmReportNavProps> = ({
  items,
  activeId,
  tone = 'light',
  sticky = true,
  offsetTop = 'var(--fm-report-nav-dock-top, var(--fm-topbar-h-measured, var(--fm-topbar-h)))',
  variant = 'surface',
  dockedSurface,
  adaptiveTheme,
  ariaLabel = 'Report sections',
  onSelect,
  backAction,
  navigationBehavior,
  testIdPrefix,
  className,
  'data-testid': dataTestId,
}) => {
  const resolvedTestId = dataTestId ?? 'fm-report-nav';
  const resolvedItemTestIdPrefix = testIdPrefix ?? resolvedTestId;
  const isDocked = variant === 'docked';
  const navRef = React.useRef<HTMLElement>(null);
  const [probeOffset, setProbeOffset] = React.useState(0);
  const itemIds = items.map(({ id }) => id).join('|');
  const resolvedActiveId = useReportNavScrollSpy({
    itemIds,
    enabled: spiesActiveSection(sticky, activeId, navigationBehavior),
    navigationRef: navRef,
    routeActiveId: activeId,
  });
  const trackRef = useReportNavTrack(resolvedActiveId);
  const requestAnchorAlignment = useSurfaceAnchorAlignment({
    navigationRef: navRef,
    itemIds,
    sticky,
    isDocked,
    navigationBehavior: resolvedNavigationBehavior(navigationBehavior),
  });

  React.useEffect(() => {
    if (!isDocked) return undefined;
    const el = navRef.current;
    if (!el) return undefined;

    const measure = (): void => {
      setProbeOffset(Math.round(el.getBoundingClientRect().bottom));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [isDocked]);

  // Publish the surface pill-bar's REAL rendered height so anchored sections
  // clear the pinned rail exactly (scroll-margin), regardless of font load,
  // responsive control swaps, or a second pill row — mirrors the top bar's
  // `--fm-topbar-h-measured` contract. The static `--fm-report-nav-h` token is
  // the pre-layout fallback. Docked navs keep their own
  // static docked-height offset and are excluded here.
  React.useEffect(() => {
    if (!sticky || isDocked) return undefined;
    const el = navRef.current;
    if (!el) return undefined;

    const publish = (): void => {
      // Only a mounted sticky nav pins under the top bar; sticky columns add
      // this height, so it exists exactly while such a nav is mounted.
      document.documentElement.style.setProperty(
        '--report-nav-pinned-h',
        `${Math.round(el.getBoundingClientRect().height)}px`,
      );
      document.documentElement.style.setProperty(
        '--fm-report-nav-h-measured',
        `${Math.round(el.getBoundingClientRect().height)}px`,
      );
    };
    publish();
    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(publish) : null;
    resizeObserver?.observe(el);
    window.addEventListener('resize', publish);
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', publish);
      document.documentElement.style.removeProperty('--fm-report-nav-h-measured');
      document.documentElement.style.removeProperty('--report-nav-pinned-h');
    };
  }, [sticky, isDocked]);

  const measuredAdaptiveTheme = useAdaptiveNavTheme({
    probeOffsetPx: probeOffset,
    scopeSelector: '.app-shell-content',
    enabled: isDocked && !adaptiveTheme,
  });
  const dockedAdaptiveTheme = adaptiveTheme ?? measuredAdaptiveTheme;
  // Only a docked nav flips its theme to the band behind it; surface navs leave
  // it unset. Compute once (used for both data-theme and data-fm-adaptive-nav).
  const dockedNavTheme = isDocked ? dockedAdaptiveTheme : undefined;

  return (
    <nav
      ref={navRef}
      className={cx(
        'fm-report-nav',
        sticky && 'fm-report-nav--sticky',
        tone === 'dark' && 'fm-report-nav--dark',
        isDocked && 'fm-report-nav--docked',
        dockedSolidClass(isDocked, dockedSurface),
        className,
      )}
      aria-label={ariaLabel}
      data-testid={resolvedTestId}
      data-theme={/* design-rescue-ok: docked transparent report nav (3jq2g.29) flips its own theme to the band behind it; route theme stays owned by FmThemeProvider. */ dockedNavTheme}
      data-fm-adaptive-nav={dockedNavTheme}
      style={sticky ? { top: offsetTop } : undefined}
    >
      <div ref={trackRef} className="fm-report-nav__track" data-testid={`${resolvedTestId}-track`}>
        <div className="fm-report-nav__rail">
        <ReportNavBackAction
          action={backAction}
          testId={`${resolvedTestId}-back`}
          // The narrow rail shows the arrow alone; the label stays the accessible name.
          labelClassName="fm-report-nav__back-label"
        />
        {items.map((item) => (
          <ReportNavItem
            key={item.id}
            item={item}
            activeId={resolvedActiveId}
            testIdPrefix={resolvedItemTestIdPrefix}
            onSelect={onSelect}
            navigationBehavior={resolvedNavigationBehavior(navigationBehavior)}
            onAnchorSelect={requestAnchorAlignment}
          />
        ))}
        </div>
      </div>
    </nav>
  );
};

// FmSectionBand / FmReportSection

// 'dark' = immersive/map band (map-charcoal). 'deep' = dark DATA surface: the
// canonical deep app background (--fm-d-bg) for tables/reports. The primitive
// owns the matching dark data-theme so routes do not create local theme islands.
// 'paper' = working evidence one step off light, under a hairline seam.
export type FmSectionBandTone = 'plain' | 'light' | 'dark' | 'deep' | 'accent' | 'paper';
export type FmSectionBandDensity = 'comfortable' | 'compact' | 'spacious';

export interface FmSectionBandProps {
  id?: string;
  eyebrow?: React.ReactNode;
  title?: React.ReactNode;
  /**
   * Heading level for the band title. The band is an h2 chapter by default; a
   * band that owns the PAGE's primary heading (a hero/lead section) passes `1`
   * so the document outline has exactly one h1 anchored to the band rather than
   * a separate stranded page-title strip. Visual styling is unchanged — only the
   * rendered tag (and thus the a11y outline) changes.
   */
  titleLevel?: 1 | 2 | 3 | 4;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  children?: React.ReactNode;
  width?: FmContentWidthSize | 'full';
  /** Semantic background tone for the full-bleed band. */
  tone?: FmSectionBandTone;
  /** Semantic vertical rhythm inside the band. */
  density?: FmSectionBandDensity;
  /** `display` sets the title at the statement tier, under a monospace eyebrow. */
  titleScale?: 'standard' | 'display';
  /**
   * `beside` places the header on a narrow start rail beside the body, the
   * editorial split at band level; it stacks below the desktop tier.
   */
  headerPlacement?: 'above' | 'beside';
  /**
   * Embedded (non-breakout) mode. Drops the full-bleed `100vw` viewport
   * breakout + report-scale section padding and renders a self-contained,
   * bordered inset surface that stays inside its parent container. Use when the
   * section is nested inside a collapse/rail/content column rather than composed
   * at the route/report level. The full-bleed default is for FmReportPage-level
   * composition only.
   *
   * DEFAULT DECISION (option a): the default stays
   * FULL-BLEED (100vw breakout) with `embedded` as the opt-out. Flipping the
   * default to non-breakout was rejected as too risky — the primitive has ~70
   * route-level full-bleed consumers that rely on the breakout, and a silent
   * flip would regress every one of them. So: whenever a FmReportSection/
   * FmSectionBand is composed INSIDE a container (collapse, rail, content
   * column, card) rather than directly at FmReportPage level, you MUST pass
   * `embedded` — otherwise it breaks out of its parent to the viewport.
   */
  embedded?: boolean;
  /**
   * Bounds the band to a token-backed max height and scrolls its overflow
   * internally (dockable side/bottom-panel behavior). Sealed DS affordance so a
   * docked panel never sets a route-local `maxHeight`/`overflowY`. `dock` = the
   * canonical side/bottom-panel dock height (`--fm-section-dock-max-h`).
   */
  maxHeight?: 'dock';
  /**
   * @internal Section-shell rail: `content` caps the foreground rail to the
   * width token (default); `wide` keeps the gutters but drops the cap. Only
   * FmSection sets it.
   */
  rail?: 'content' | 'wide';
  headerAside?: React.ReactNode;
  /**
   * @internal DS/migration class hook; forbidden in route-owned code. Add a
   * named section variant or semantic prop instead.
   */
  className?: string;
  /**
   * @internal DS-internal body class hook for migration adapters; forbidden in
   * route-owned code because the band owns body layout.
   */
  bodyClassName?: string;
  'data-testid'?: string;
}

function sectionBandClassName(args: {
  tone: FmSectionBandTone;
  density: FmSectionBandDensity;
  titleScale?: 'standard' | 'display';
  headerPlacement?: 'above' | 'beside';
  width: FmContentWidthSize | 'full';
  embedded?: boolean;
  maxHeight?: 'dock';
  /**
   * @internal DS-internal className prop; forbidden in route-owned code.
   */
  className?: string;
}): string {
  return cx(
    'fm-section-band',
    `fm-section-band--${args.tone}`,
    `fm-section-band--${args.density}`,
    args.titleScale === 'display' && 'fm-section-band--display-title',
    args.headerPlacement === 'beside' && 'fm-section-band--header-beside',
    args.width === 'full' && 'fm-section-band--full',
    args.embedded && 'fm-section-band--embedded',
    args.maxHeight && `fm-section-band--max-${args.maxHeight}`,
    args.className,
  );
}

/**
 * Resolve the content-rail inline style. Full-bleed bands center an inner rail
 * capped to the semantic content width; embedded (non-breakout) sections fill
 * their parent and take rail sizing from CSS. Kept as a module-level helper so
 * the FmSectionBand component body stays within the complexity budget.
 */
function sectionBandInnerStyle(
  embedded: boolean | undefined,
  railWidth: FmContentWidthSize,
  rail: 'content' | 'wide' | undefined,
): React.CSSProperties | undefined {
  if (embedded || rail === 'wide') return undefined;
  return { maxWidth: sectionBandInnerMaxWidth(railWidth) };
}

interface SectionBandHeaderProps {
  resolvedTestId?: string;
  headingId?: string;
  eyebrow?: React.ReactNode;
  title?: React.ReactNode;
  titleLevel?: 1 | 2 | 3 | 4;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  headerAside?: React.ReactNode;
}

const SectionBandHeader: React.FC<SectionBandHeaderProps> = ({
  resolvedTestId,
  headingId,
  eyebrow,
  title,
  titleLevel = 2,
  description,
  actions,
  headerAside,
}) => {
  const TitleTag = `h${titleLevel}` as 'h1' | 'h2' | 'h3' | 'h4';
  // A section prints ONE title. An eyebrow that repeats it is not a kicker, it
  // is the title said twice in two type sizes — so it is dropped here rather
  // than left for every call site to remember.
  const eyebrowText = plainHeadingText(eyebrow);
  const echoesTitle = eyebrowText !== null && eyebrowText === plainHeadingText(title);
  return (
  <div
    className="fm-section-band__header"
    data-testid={resolvedTestId ? `${resolvedTestId}-header` : undefined}
  >
    <div className="fm-section-band__heading">
      {eyebrow && !echoesTitle ? (
        <span className="fm-section-band__eyebrow">{eyebrow}</span>
      ) : null}
      {title ? <TitleTag id={headingId} className="fm-section-band__title">{title}</TitleTag> : null}
      {description ? (
        <p className="fm-section-band__description">{description}</p>
      ) : null}
    </div>
    {(actions || headerAside) ? (
      <div className="fm-section-band__aside">
        {headerAside}
        {actions}
      </div>
    ) : null}
  </div>
  );
};

const SectionBandContent: React.FC<SectionBandHeaderProps & {
  hasHeader: boolean;
  children?: React.ReactNode;
  bodyClassName?: string;
}> = ({ hasHeader, children, bodyClassName, resolvedTestId, ...header }) => (
  <>
    {hasHeader ? (
      <SectionBandHeader resolvedTestId={resolvedTestId} {...header} />
    ) : null}
    {children !== undefined && children !== null ? (
      <div
        className={cx('fm-section-band__body', bodyClassName)}
        data-testid={resolvedTestId ? `${resolvedTestId}-body` : undefined}
      >
        {children}
      </div>
    ) : null}
  </>
);

export const FmSectionBand: React.FC<FmSectionBandProps> = ({
  id,
  eyebrow,
  title,
  titleLevel,
  description,
  actions,
  children,
  width = 'content',
  tone = 'light',
  density = 'comfortable',
  titleScale,
  headerPlacement,
  embedded,
  maxHeight,
  rail,
  headerAside,
  className,
  bodyClassName,
  'data-testid': dataTestId,
}) => {
  const hasHeader = Boolean(eyebrow || title || description || actions || headerAside);
  const headingId = id && title ? `${id}-heading` : undefined;
  const resolvedTestId = dataTestId ?? (id ? `fm-section-${id}` : undefined);
  const railWidth: FmContentWidthSize = width === 'full' ? 'content' : width;
  const sectionTheme = resolveSectionTheme(embedded, tone);
  const hostedHeading = useSectionHostedHeading(title, titleLevel ?? 2);
  const content = (
    <SectionBandContent
      hasHeader={hasHeader}
      bodyClassName={bodyClassName}
      resolvedTestId={resolvedTestId}
      headingId={headingId}
      eyebrow={eyebrow}
      title={title}
      titleLevel={titleLevel}
      description={description}
      actions={actions}
      headerAside={headerAside}
    >
      {children}
    </SectionBandContent>
  );

  return (
    <section
      id={id}
      aria-labelledby={headingId}
      className={sectionBandClassName({
        tone,
        density,
        titleScale,
        headerPlacement,
        width,
        embedded,
        maxHeight,
        className,
      })}
      data-testid={resolvedTestId}
      data-theme={/* design-rescue-ok: FmSectionBand owns dark/deep content-band token resolution; route theme remains FmThemeProvider-owned. */ sectionTheme}
    >
      <div
        className="fm-section-band__inner"
        style={sectionBandInnerStyle(embedded, railWidth, rail)}
      >
        {/* A titled band publishes the heading it printed, so a data block
            composed inside it renders titleless instead of reprinting it.
            An UNTITLED band publishes nothing and lets
            its own host, if any, keep applying to its children. */}
        <FmHostedHeadingProvider value={hostedHeading}>
          {content}
        </FmHostedHeadingProvider>
      </div>
    </section>
  );
};

export interface FmReportSectionProps extends Omit<FmSectionBandProps, 'tone'> {
  tone?: Exclude<FmSectionBandTone, 'plain'>;
}

export const FmReportSection: React.FC<FmReportSectionProps> = ({
  tone = 'light',
  ...props
}) => <FmSectionBand tone={tone} {...props} />;

